<template>
  <div class="pgp-card" :data-tool-key="toolKey">
    <div class="pgp-edge" :class="`edge-${toolKey}`"></div>
    <div class="tool-icon">{{ toolInitials }}</div>
    <div class="body-area">
      <div class="top-line">
        <span class="tool-name">{{ analysis.tool }}</span>
        <span class="expires-inline">– {{ pendingFooter ?? `Expires ${expiresLabel}` }}</span>
        <span class="top-line-spacer"></span>
      </div>

      <div v-if="hasAnyMetaField" class="meta-grid">
        <div v-if="termValue" class="meta-item">
          <span class="meta-label">Term</span>
          <span class="meta-value">{{ termValue }}</span>
        </div>
        <div v-if="sumValue" class="meta-item">
          <span class="meta-label">Sum assured</span>
          <span class="meta-value">{{ sumValue }}</span>
        </div>
        <div v-if="benefitValue" class="meta-item">
          <span class="meta-label">Benefit</span>
          <span class="meta-value">{{ benefitValue }}</span>
        </div>
        <div v-if="deferredValue" class="meta-item">
          <span class="meta-label">Deferred</span>
          <span class="meta-value">{{ deferredValue }}</span>
        </div>
        <div v-if="coverBasisValue" class="meta-item">
          <span class="meta-label">Cover basis</span>
          <span class="meta-value">{{ coverBasisValue }}</span>
        </div>
        <div v-if="interestRateValue" class="meta-item">
          <span class="meta-label">Interest rate</span>
          <span class="meta-value">{{ interestRateValue }}</span>
        </div>
        <div v-if="indexationValue" class="meta-item">
          <span class="meta-label">Indexation</span>
          <span class="meta-value">{{ indexationValue }}</span>
        </div>
        <div v-if="premiumTypeValue" class="meta-item">
          <span class="meta-label">Premium type</span>
          <span class="meta-value">{{ premiumTypeValue }}</span>
        </div>
      </div>

    </div>

    <div class="integration-area">
      <span v-if="analysis.integration === 'UNDERWRITEME'" class="integration-logo integration-logo--ume">
        <img :src="umeLogo" alt="UnderwriteMe" />
      </span>
      <span v-else-if="analysis.integration === 'IPIPELINE'" class="integration-logo integration-logo--ipipeline">
        <img :src="ipipelineLogo" alt="iPipeline" />
      </span>
      <span v-else class="pill-quality">Quality only</span>
    </div>

    <div class="actions">
      <button
        v-if="analysis.isPending"
        class="btn btn-pending"
        @click="$emit('complete-underwriting', analysis.externalApplicationUrl)"
      >
        Complete underwriting
      </button>
      <template v-else>
        <button class="btn" data-test="view-results" @click="onViewResults">View results</button>
        <slot name="reports-action" :reports-count="reportsCount" />
      </template>
    </div>
  </div>
</template>

<script>
import umeLogo from '../assets/images/underWriteMeLogo3.png';
import ipipelineLogo from '../assets/images/iPipelineLogo.png';
import {
  formatTerm,
  formatCurrency,
  prettyIndexation,
  prettyDeferredPeriod,
  prettyCoverBasis,
} from './analysisCardFormatters';

const TOOL_KEY_BY_NAME = {
  'Mortgage Protection': 'mp',
  'Critical Illness': 'ci',
  'Life Protection': 'lp',
  'Income Protection': 'ip',
  'Family Income Benefit': 'fib',
  'Business Protection': 'bp',
};

export default {
  name: 'AnalysisCard',
  props: {
    analysis: { type: Object, required: true },
    reportsCount: { type: Number, default: 0 },
  },
  emits: ['view-results', 'complete-underwriting'],
  data() {
    return { umeLogo, ipipelineLogo };
  },
  computed: {
    toolKey() {
      return TOOL_KEY_BY_NAME[this.analysis.tool] ?? 'mp';
    },
    toolInitials() {
      return (this.toolKey ?? 'mp').toUpperCase();
    },
    termValue() { return formatTerm(this.analysis.term); },
    sumValue() { return formatCurrency(this.analysis.sumAssured); },
    benefitValue() {
      const isMonthly = !!this.analysis.benefitBasis || !!this.analysis.deferredPeriod;
      return formatCurrency(this.analysis.benefitAmount, { monthly: isMonthly });
    },
    deferredValue() { return prettyDeferredPeriod(this.analysis.deferredPeriod); },
    coverBasisValue() { return prettyCoverBasis(this.analysis.coverBasis); },
    interestRateValue() {
      const v = this.analysis.interestRate;
      return v ? `${v}%` : null;
    },
    indexationValue() { return prettyIndexation(this.analysis.indexation); },
    premiumTypeValue() { return this.analysis.premiumType || null; },
    hasAnyMetaField() {
      return !!(
        this.termValue || this.sumValue || this.benefitValue ||
        this.deferredValue || this.coverBasisValue || this.interestRateValue ||
        this.indexationValue || this.premiumTypeValue
      );
    },
    expiresLabel() {
      const d = new Date(this.analysis.expires);
      if (Number.isNaN(d.getTime())) return '';
      return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    },
    pendingFooter() {
      return this.analysis.isPending ? 'Waiting for underwriting completion' : null;
    },
  },
  methods: {
    onViewResults() {
      this.$emit('view-results', {
        customerReference: this.analysis.customerReference,
        analysisReference: this.analysis.analysisReference,
      });
    },
  },
};
</script>

<style scoped>
.pgp-card {
  background: #fff;
  border: 1px solid #e9eaf0;
  border-radius: 12px;
  padding: 12px 16px;
  margin-bottom: 10px;
  display: grid;
  grid-template-columns: 5px 40px 1fr auto auto;
  gap: 14px;
  align-items: center;
  box-shadow: 0 1px 2px rgba(20, 20, 40, 0.04);
  transition: box-shadow 0.15s, transform 0.15s;
}
.integration-area {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 140px;
  box-sizing: border-box;
  padding: 0 8px;
  margin-right: 24px;
}
.expires-inline {
  font-size: 11px;
  font-weight: 400;
  color: #8b90a3;
  white-space: nowrap;
}
.pgp-card:hover { box-shadow: 0 4px 12px rgba(20, 20, 40, 0.07); transform: translateY(-1px); }
.pgp-edge { border-radius: 12px; height: 100%; min-height: 44px; align-self: stretch; }
.edge-mp { background: #3B6FE3; } /* blue — financial / mortgage */
.edge-ci { background: #E54B6B; } /* red — urgency / severity */
.edge-lp { background: #2EAA75; } /* green — life / growth */
.edge-ip { background: #D4A015; } /* gold — income / earnings */
.edge-fib { background: #D45B7E; } /* coral — family / warmth */
.edge-bp { background: #4B5E7A; } /* slate — business / corporate */

.tool-icon {
  width: 40px; height: 40px; border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
  font-weight: 700; font-size: 13px; letter-spacing: 0.5px;
  background: #F4F5FA; color: #353C8E;
}
.body-area { display: flex; flex-direction: column; gap: 6px; min-width: 0; }
.top-line { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.top-line-spacer { flex: 1; }
.tool-name { font-size: 15px; font-weight: 600; color: #1a1d2b; letter-spacing: -0.2px; }

.integration-logo { display: flex; align-items: center; padding: 2px 0; opacity: 0.9; }
.integration-logo img { height: 100%; width: auto; display: block; }
/* UME has two-line composition ("protection platform / from UnderwriteMe") so it needs more vertical room. */
.integration-logo--ume { height: 38px; }
/* iPipeline is a single-line wide logo, give it less vertical space so widths visually balance. */
.integration-logo--ipipeline { height: 20px; }

.pill-quality {
  background: #353C8E; color: #FFFFFF; padding: 5px 11px; border-radius: 20px;
  font-size: 11px; font-weight: 600; letter-spacing: 0.3px;
  height: 24px; display: inline-flex; align-items: center;
}
.meta-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 12px;
  align-items: baseline;
}
.meta-item { display: flex; align-items: baseline; gap: 5px; flex: 0 0 140px; min-width: 0; }
.meta-label { font-size: 11px; color: #9298ad; font-weight: 400; letter-spacing: 0; }
.meta-value { font-size: 13px; font-weight: 600; color: #2a2d38; }

.actions { display: flex; flex-direction: row; gap: 18px; align-items: center; justify-content: flex-end; width: 220px; }
.btn {
  background: transparent;
  color: #353C8E;
  padding: 6px 2px;
  border: 0;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  transition: color 0.15s;
}
.btn::after {
  content: '→';
  display: inline-block;
  transition: transform 0.15s;
}
.btn:hover { color: #1f266e; }
.btn:hover::after { transform: translateX(2px); }
.btn-pending { color: #B23B3B; font-weight: 700; }
.btn-pending:hover { color: #8a2828; }
</style>
